import type { TeamTab, SalesMetrics, RepairMetrics, InstallationMetrics } from '../types';

interface EmployeeCardProps {
  employee: SalesMetrics | RepairMetrics | InstallationMetrics;
  rank: number;
  type: TeamTab;
}

export function EmployeeCard({ employee, rank, type }: EmployeeCardProps) {
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    return parts.map(p => p.charAt(0)).join('').substring(0, 2).toUpperCase();
  };

  // Get display photo: customPhoto > HCP avatar > null
  const getDisplayPhoto = (): string | null => {
    // Custom photo takes priority (stored as base64)
    if (employee.customPhoto) {
      return `data:image/jpeg;base64,${employee.customPhoto}`;
    }
    // Fall back to HCP avatar, filter out placeholders
    if (employee.avatar && !employee.avatar.includes('add_image_thumb')) {
      return employee.avatar;
    }
    return null;
  };

  const getConversionColorClass = (color?: string) => {
    if (color === 'green') return 'conversion-green';
    if (color === 'yellow') return 'conversion-yellow';
    if (color === 'red') return 'conversion-red';
    return '';
  };

  const renderMetrics = () => {
    if (type === 'sales') {
      const e = employee as SalesMetrics;
      return (
        <>
          {/* MAIN METRIC - Large display */}
          <div className="main-metric">
            <span className="main-metric-value">${e.estimatesWonAmount.toLocaleString()}</span>
            <span className="main-metric-label">Won Amount</span>
          </div>
          {/* Secondary metrics as tiles */}
          <div className="metrics-grid">
            <div className="metric">
              <div className="metric-value">
                <span className="won">{e.estimatesWon}</span>
                {' / '}
                <span className="open">{e.estimatesOpen}</span>
                {' / '}
                <span className="lost">{e.estimatesLost}</span>
              </div>
              <div className="metric-label">W / O / L</div>
            </div>
            <div className={`metric ${getConversionColorClass(e.conversionColor)}`}>
              <div className="metric-value">{e.conversionRate}%</div>
              <div className="metric-label">Conversion</div>
            </div>
            <div className="metric">
              <div className="metric-value">${e.avgTicket.toLocaleString()}</div>
              <div className="metric-label">Avg Ticket</div>
            </div>
          </div>
        </>
      );
    }

    if (type === 'repair') {
      const e = employee as RepairMetrics;
      return (
        <>
          {/* MAIN METRIC - Large display */}
          <div className="main-metric">
            <span className="main-metric-value">${e.estimatesWonAmount.toLocaleString()}</span>
            <span className="main-metric-label">Won Amount</span>
          </div>
          {/* Secondary metrics as tiles */}
          <div className="metrics-grid">
            <div className="metric">
              <div className="metric-value">{e.serviceCalls}</div>
              <div className="metric-label">Service Calls</div>
            </div>
            <div className="metric">
              <div className="metric-value">${e.serviceCallsRevenue.toLocaleString()}</div>
              <div className="metric-label">Revenue</div>
            </div>
            <div className={`metric ${e.callbacks > 0 ? 'callback-metric' : ''}`}>
              <div className="metric-value">{e.callbacks}</div>
              <div className="metric-label">Callbacks ({e.callbacksPercent}%)</div>
            </div>
            <div className="metric">
              <div className="metric-value">{e.estimatesWon}/{e.estimatesLost}</div>
              <div className="metric-label">Won/Lost</div>
            </div>
            <div className={`metric ${getConversionColorClass(e.conversionColor)}`}>
              <div className="metric-value">{e.conversionRate}%</div>
              <div className="metric-label">Conversion</div>
            </div>
          </div>
        </>
      );
    }

    // Installation
    const e = employee as InstallationMetrics;
    return (
      <>
        {/* MAIN METRIC - Large display */}
        <div className="main-metric">
          <span className="main-metric-value">{e.totalInstallationJobs}</span>
          <span className="main-metric-label">Installation Jobs</span>
        </div>
        {/* Secondary metrics as tiles */}
        <div className="metrics-grid">
          <div className={`metric ${e.callbacks > 0 ? 'callback-metric' : ''}`}>
            <div className="metric-value">{e.callbacks}</div>
            <div className="metric-label">Callbacks ({e.callbacksPercent}%)</div>
          </div>
          <div className={`metric ${e.failedInspections > 0 ? 'failed-metric' : ''}`}>
            <div className="metric-value">{e.failedInspections}</div>
            <div className="metric-label">Failed Inspections</div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="employee-card">
      <div className="employee-header">
        <div className="employee-name">{employee.employeeName}</div>
        <div className="employee-rank">#{rank}</div>
      </div>
      <div className="employee-photo">
        {getDisplayPhoto() ? (
          <img
            src={getDisplayPhoto()!}
            alt={employee.employeeName}
          />
        ) : (
          <div className="avatar-placeholder">
            {getInitials(employee.employeeName)}
          </div>
        )}
      </div>
      {renderMetrics()}
    </div>
  );
}
